import { sortBy, uniqBy, groupBy, keys, findIndex } from 'lodash';
import { getTimestamp } from 'app/lib/time';
import * as Analytics from 'app/lib/analytics';
import heydoctor from 'app/vendor/heydoctor';
import { Stores, Store } from 'app/lib/synaptik';
import moment from 'moment';
import { Message } from '@goodrx/heydoctor-sdk';
import { MessageTypes } from '@goodrx/heydoctor-core';
import { PhotoUploadMessage, RequestPhotoMessage, RetakePhotoMessage } from '@goodrx/heydoctor-sdk/es/models/message';

interface State {
  isChatOpen: boolean;
  messages: Message[];
  messagesByDate?: Record<string, Message[]>;
  cachedTotal?: number;
  fetchingOlder?: boolean;
  sending?: boolean;
  fetchingLatest?: boolean;
}

export class ChatStore extends Store<State, Stores> {
  subscription: any;

  state: State = {
    isChatOpen: false,
    messages: [],
    messagesByDate: {},
    cachedTotal: undefined,
  };

  subscribe = () => {
    if (this.subscription) return;

    this.fetchLatestMessages();
    this.subscription = setInterval(this.fetchLatestMessages, 2500);
  };

  unsubscribe = () => {
    clearInterval(this.subscription);
    this.setState({ messages: [], cachedTotal: undefined });
    this.subscription = null;
  };

  fetch = async (params: any) => heydoctor.messages.fetchList(params);

  fetchOlderMessages = () =>
    this.runAsync({
      key: 'fetchingOlder',
      log: false,
      work: async () => {
        const { messages } = this.state;
        const query: { order: string; limit: number; before?: number } = {
          order: 'desc',
          limit: 25,
        };

        if (messages.length) {
          query.before = messages[0].id;
        }

        const { messages: olderMessages } = await this.fetch(query);
        await this.setMessages(olderMessages);
      },
    });

  fetchLatestMessages = () =>
    this.runAsync({
      key: 'fetchingLatest',
      log: false,
      work: async () => {
        if (document.hidden) return;

        const { messages } = this.state as any;

        const query: any = {
          polling: true,
          order: 'desc',
          limit: 25,
        };

        if (messages.length) {
          query.after = messages[messages.length - 1].id;
        }

        const { messages: latestMessages, paging } = await this.fetch(query);

        if (!this.state.cachedTotal) {
          this.setState({ cachedTotal: paging.total });
        }

        this.setMessages(latestMessages);
      },
    });

  send = (body: any) => {
    this.runAsync({
      key: 'sending',
      work: async () => {
        await heydoctor.messages.send(body);
        this.fetchLatestMessages();
        Analytics.onMessageSent();
      },
    });
  };

  setMessages = (newMessages: Message[] = []) => {
    const { messages } = this.state;
    if (!newMessages.length) return null;

    const sortedAndDedupedMessages = sortBy(uniqBy([...newMessages, ...messages], 'id'), 'id');
    const groupedMessages = groupBy(sortedAndDedupedMessages, (m) =>
      getTimestamp(moment.utc(m.createdAt).local(), 'YYYY-MM-DD')
    );

    return this.setState({
      messages: sortedAndDedupedMessages,
      messagesByDate: groupedMessages,
    });
  };

  markAsRead = async (message: { markAsRead: () => any }) => message.markAsRead();

  markPhotoRequestAsCompleted = (messageId: number) => {
    const message = this.state.messages.find((m) => m.id === messageId);
    if (message?.photo) {
      message.photo.status = 'uploaded';
      this.setMessages([message]);
    }
  };

  hasPatientRespondedToPhotoRequest = (message: RequestPhotoMessage | RetakePhotoMessage) => {
    const photoUploadsAfterGivenMessage = this.state.messages.filter(
      (m) => m.type === MessageTypes.PHOTO_UPLOAD && new Date(m.createdAt) > new Date(message.createdAt)
    );
    return photoUploadsAfterGivenMessage.some((m: PhotoUploadMessage) => m.photo.id === message.photo.id);
  };

  hasUnreadMessages = () => heydoctor.messages.hasUnreadMessages();

  /**
   * Returns whether or not there is a later message than the passed message in the chat. Compares by timestamp.
   * @param { message } - the message to compare
   */
  hasLaterMessage = ({ message }: { message: Message }) => {
    const [latestMessageGroup] = keys(this.state.messagesByDate).slice(-1);
    const latestMessages = this.state.messagesByDate?.[latestMessageGroup];
    return findIndex(latestMessages, (m) => moment(m.createdAt).isAfter(message.createdAt)) >= 0;
  };

  toggleChat = () => {
    this.setState({
      isChatOpen: !this.state.isChatOpen,
    });
  };
}
